// Motion math for live instruments (no DOM, no React, unit-tested).
//
// The problem this solves: samples arrive from the boat in discrete batches,
// but an analog instrument moves continuously. Driving a CSS transition from
// each arriving sample restarts the transition mid-flight, so the needle
// rubber-bands — it never reaches one target before being aimed at the next.
//
// Two stages, both pure:
//
//   1. TIMELINE (only when samples arrive close together — see
//      `TIMELINE_MAX_GAP_MS`). Buffer samples with their real instrument-read
//      times and play them back on a fixed lag (`valueAt`). Rendering
//      "now − 300 ms" means there is almost always a sample on each side of the
//      playhead to interpolate between. The cost is a constant, honest 300 ms
//      of latency; the alternative is extrapolating, which invents readings.
//      When samples are further apart than the spring's settle time this stage
//      is skipped — stage 2 alone already fills the gap, without the lag.
//
//   2. SPRING. Feed the timeline's output to a critically-damped spring
//      (`stepSpring`). Linear interpolation has corners at each sample; the
//      spring rounds them into continuous motion without overshoot, which is
//      what makes it read as an instrument rather than an animation.
//
// Angles are handled by UNWINDING at push time: a stored angle series is
// continuous and unbounded (350 → 10 stores as 370), so interpolation and the
// spring both work on plain numbers and a needle crossing north sweeps 2°
// rather than spinning 358° backwards.

import { signedDelta, unwindAngle } from './arc';

/** One buffered reading: a value and the wall-clock time to DISPLAY it at. */
export interface MotionSample {
  /** Display time (ms, `performance.timeOrigin`-free — plain `Date.now()`). */
  at: number;
  /** For angular series this is the UNWOUND value, not a 0–360 bearing. */
  value: number;
}

/** Spring state. `v` is in units/second. */
export interface SpringState {
  x: number;
  v: number;
}

/**
 * Angular frequency of the follower spring, in rad/s.
 *
 * At critical damping the settling time is roughly 5/ω, so 14 gives ~350 ms to
 * rest. Slower reads as syrup on a fast-moving needle; faster reintroduces the
 * jitter the spring exists to smooth away.
 */
export const OMEGA = 14;

/**
 * Largest timestep the spring will integrate in one go, in seconds.
 *
 * A backgrounded tab delivers one enormous frame on return. The closed form
 * below is stable for any dt, but integrating 30 s of "catch-up" in one step
 * would snap the needle across the dial. Clamping makes a long gap behave like
 * a normal frame — and `snapSpring` handles the tab-restore case properly.
 */
export const MAX_STEP_S = 0.1;

/**
 * How far behind real time the playhead sits, in ms, when playing a timeline.
 *
 * Must exceed one inter-sample gap plus network jitter, or the playhead
 * outruns the buffer and the needle freezes between samples. Perceived lag is
 * this plus the spring's settling time.
 */
export const LAG_MS = 300;

/**
 * The largest inter-sample gap timeline playback is worth using for.
 *
 * Interpolation only earns its lag when samples arrive CLOSER together than
 * the spring's own settling time (~350 ms). Below that, the spring alone would
 * step visibly between readings, so playing a lagged timeline buys real
 * smoothness for 300 ms of delay. Above it, the spring already covers the gap
 * continuously — and holding a playhead a full gap behind would mean showing
 * wind from a second ago for no gain at all.
 *
 * So the renderer picks its mode from the OBSERVED gap rather than assuming a
 * feed rate. That is what lets the boat change its tick (it went 5 Hz → 1 Hz)
 * without the browser stalling: at 200 ms it interpolates, at 1 s it springs
 * straight at the newest reading. Neither end has to know the other's number.
 */
export const TIMELINE_MAX_GAP_MS = 400;

/**
 * Median gap between the buffer's most recent samples, or null if there are
 * too few to tell. Median, not mean, so one late frame does not swing the
 * mode decision.
 */
export function recentGapMs(buffer: readonly MotionSample[], window = 6): number | null {
  const n = buffer.length;
  if (n < 2) return null;
  const from = Math.max(1, n - window);
  const gaps: number[] = [];
  for (let i = from; i < n; i++) gaps.push(buffer[i]!.at - buffer[i - 1]!.at);
  if (gaps.length === 0) return null;
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)]!;
}

/** Samples older than this behind the playhead are pruned. Two lags of slack
 *  so an out-of-order or slightly-late frame still finds its neighbours. */
const RETAIN_MS = LAG_MS * 3;

/**
 * Advance a critically-damped spring by `dt` seconds toward `target`.
 *
 * Exact closed-form solution of `x'' + 2ω x' + ω² x = ω² T`, not an Euler
 * step — so it is stable at any framerate and cannot overshoot or ring, which
 * a naive integrator does exactly when frames get long.
 */
export function stepSpring(state: SpringState, target: number, dt: number): SpringState {
  const step = dt <= 0 ? 0 : dt > MAX_STEP_S ? MAX_STEP_S : dt;
  if (step === 0) return state;
  const d = state.x - target;
  const c2 = state.v + OMEGA * d;
  const e = Math.exp(-OMEGA * step);
  const dNext = d + c2 * step;
  return { x: target + dNext * e, v: (c2 - OMEGA * dNext) * e };
}

/** Is the spring close enough to its target to stop animating?
 *
 *  Idle gauges must burn zero frames — a dashboard of instruments each holding
 *  a rAF loop open forever is a battery bug. The thresholds are below one
 *  device pixel of movement at instrument scale. */
export function springSettled(state: SpringState, target: number, epsilon = 0.01): boolean {
  return Math.abs(state.x - target) < epsilon && Math.abs(state.v) < epsilon;
}

/** Jump the spring to a value with no motion (first sample, tab restore,
 *  reduced-motion). Zeroing velocity is the point: carrying it over would
 *  fling the needle away from the value we just snapped to. */
export function snapSpring(x: number): SpringState {
  return { x, v: 0 };
}

/**
 * Append a sample to a timeline buffer, unwinding angles and pruning old
 * entries. Returns a NEW array; the input is not mutated.
 *
 * Idempotent on a repeated `at`: React 18 StrictMode double-invokes effects,
 * and a sample pushed twice would otherwise sit in the buffer twice and skew
 * interpolation. Replacing same-timestamp entries also gives a late correction
 * for an already-buffered instant the right behavior.
 */
export function pushSample(
  buffer: readonly MotionSample[],
  at: number,
  value: number,
  opts: { angular?: boolean; now?: number } = {}
): MotionSample[] {
  const last = buffer[buffer.length - 1];
  // Unwind against the previous stored value so the series stays continuous:
  // a 350 → 10 step is stored as 370, and interpolation crosses north the
  // short way instead of running the long way round the dial.
  const stored =
    opts.angular === true && last !== undefined ? unwindAngle(last.value, value) : value;

  let next: MotionSample[];
  if (last !== undefined && at === last.at) {
    next = buffer.slice(0, -1);
    next.push({ at, value: stored });
  } else if (last !== undefined && at < last.at) {
    // Out of order: splice into place rather than dropping it. Rare, but a
    // dropped sample leaves a gap the playhead will sit in.
    next = buffer.slice();
    let i = next.length;
    while (i > 0 && next[i - 1]!.at > at) i--;
    next.splice(i, 0, { at, value: stored });
  } else {
    next = buffer.slice();
    next.push({ at, value: stored });
  }

  const cutoff = (opts.now ?? at) - LAG_MS - RETAIN_MS;
  let drop = 0;
  // Keep one sample older than the cutoff — it is the left bracket the
  // playhead needs while the next sample is still in flight.
  while (drop + 1 < next.length && next[drop + 1]!.at <= cutoff) drop++;
  return drop > 0 ? next.slice(drop) : next;
}

/**
 * The interpolated value at `t`, or null if the buffer is empty.
 *
 * Holds at both ends rather than extrapolating: before the first sample and
 * after the last, the instrument shows the reading it actually has. When the
 * feed stops, this makes the needle FREEZE at the last real value — which is
 * the honest display, with staleness dimming carrying the message that the
 * data has stopped.
 */
export function valueAt(buffer: readonly MotionSample[], t: number): number | null {
  if (buffer.length === 0) return null;
  const first = buffer[0]!;
  if (t <= first.at) return first.value;
  const last = buffer[buffer.length - 1]!;
  if (t >= last.at) return last.value;

  // Linear scan from the end: the playhead is always near the newest samples,
  // so this is O(1) in practice on a buffer that only ever holds ~10 entries.
  for (let i = buffer.length - 1; i > 0; i--) {
    const b = buffer[i]!;
    const a = buffer[i - 1]!;
    if (t >= a.at && t <= b.at) {
      const span = b.at - a.at;
      if (span <= 0) return b.value;
      return a.value + ((t - a.at) / span) * (b.value - a.value);
    }
  }
  return first.value;
}

/**
 * Re-anchor an unwound angle series near a bearing, dropping accumulated
 * turns. Returns a new buffer, or the same one if no shift is needed.
 *
 * A needle spinning one way for hours accumulates thousands of degrees. That
 * is harmless for the math but eventually costs float precision, and it means
 * a fresh subscriber and a long-running one disagree about the same bearing.
 * Shifting by a whole multiple of 360 is visually invisible.
 */
export function reanchor(buffer: readonly MotionSample[], limit = 720): MotionSample[] {
  const last = buffer[buffer.length - 1];
  if (last === undefined || Math.abs(last.value) <= limit) return buffer as MotionSample[];
  const turns = Math.round(last.value / 360);
  const shift = turns * 360;
  return buffer.map((s) => ({ at: s.at, value: s.value - shift }));
}

/**
 * Convert one frame's boat-clock sample times into local DISPLAY times.
 *
 * Deliberately NOT clock-offset estimation. The boat's clock and the browser's
 * disagree by an unknown, drifting amount, and estimating that offset means a
 * feedback loop that misbehaves exactly when the network hiccups. Instead we
 * take one fact we can trust — the RELATIVE spacing of samples within a frame
 * — and anchor the newest one at the moment the frame arrived:
 *
 *     displayAt_i = receivedAt − (b_last − b_i)
 *
 * So a batch lands as samples correctly spaced and ending "now", regardless
 * of clock skew. Spacing is what the interpolator needs; absolute agreement is not.
 *
 * Every result is ≤ `receivedAt` by construction: a display time may not sit
 * in the future, because the playhead is `now − LAG_MS` and a future-stamped
 * sample would be one the playhead never reaches.
 *
 * Ordering is deliberately NOT enforced here. Each PATH is its own timeline —
 * an anemometer and a compass stamp independently — so a floor shared across
 * a whole frame would ratchet every path forward by the spread of the fastest
 * one. Clamp per path with {@link nextDisplayAt} instead.
 */
export function retimeBatch(times: readonly number[], receivedAt: number): number[] {
  if (times.length === 0) return [];
  let newest = times[0]!;
  for (const t of times) if (t > newest) newest = t;
  return times.map((t) => receivedAt - (newest - t));
}

/**
 * Clamp one path's next display time: strictly after its own previous one, and
 * never later than arrival.
 *
 * The upper cap is the important half. Without it, nudging each sample past
 * its predecessor lets a path's timeline drift into the future — and since the
 * playhead is fixed at `now − LAG_MS`, a timeline running ahead of `now` means
 * the playhead falls further and further behind, replaying old readings in
 * slow motion while the numerals show the truth. That is a stalled needle, not
 * a smooth one.
 */
export function nextDisplayAt(
  candidate: number,
  previous: number | undefined,
  cap: number
): number {
  let at = candidate;
  if (previous !== undefined && at <= previous) at = previous + 1;
  return at > cap ? cap : at;
}

/** Shortest signed rotation, re-exported so component code has one import for
 *  everything motion-related. */
export { signedDelta, unwindAngle };
